import React from "react";
import moment from "moment";
import DOMPurify from "dompurify";

import { sanitizeURLs } from "../../utils/helpers";
import { Message as MessageInterface } from "../../types/collaborations/chat";

type Props = {
    groupedMessages: Record<string, MessageInterface[]>;
};

// DOMPurify types expect mutable string[] (not readonly tuples)
const allowedTags: string[] = ["b", "i", "u", "strong", "em", "br", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "p", "a"];
const allowedAttr: string[] = ["href", "rel", "target"];

function getDividerLabel(dateIso: string) {
    const m = moment(dateIso);
    if (m.isSame(moment(), "day")) return "Today";
    return `${m.format("ddd")}, ${m.format("DD/MM")}`;
}

function sanitizeMessageHtml(input: string) {
    return DOMPurify.sanitize(sanitizeURLs(input ?? ""), {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: allowedAttr,
    });
}

export default function RoomMessages({ groupedMessages }: Props) {
    const dateKeys = Object.keys(groupedMessages);

    return (
        <div className="tt-chat-room">
            <div className="tt-chat-room-inner">
                {dateKeys.map((dateKey) => (
                    <React.Fragment key={dateKey}>
                        <div className="tt-chat-room-divider" aria-label={`Messages for ${getDividerLabel(dateKey)}`}>
                            <div className="tt-chat-room-divider-line" />
                            <div className="tt-chat-room-divider-label">{getDividerLabel(dateKey)}</div>
                            <div className="tt-chat-room-divider-line" />
                        </div>

                        {groupedMessages[dateKey].map((msg, i) => {
                            const prev = groupedMessages[dateKey][i - 1];
                            const direction = msg.props.model.direction;
                            const isOutgoing = direction === "outgoing";
                            const senderName = msg.props.model.sender ?? "";
                            const isPrevIncomingSameSender =
                                !!prev &&
                                prev.props.model.direction === "incoming" &&
                                direction === "incoming" &&
                                (prev.props.model.sender ?? "") === senderName;
                            const showIncomingSenderLabel = direction === "incoming" && !!senderName && !isPrevIncomingSameSender;
                            const time = msg.props.model.sentTime;
                            const html = sanitizeMessageHtml(msg.props.model.message ?? "");

                            return (
                                <React.Fragment key={`${dateKey}-${i}`}>
                                    {showIncomingSenderLabel ? (
                                        <div className="tt-chat-room-sender-group-label">{senderName}</div>
                                    ) : null}

                                    <div
                                        className={`tt-chat-room-bubble-row ${isOutgoing ? "tt-chat-room-bubble-row--outgoing" : "tt-chat-room-bubble-row--incoming"} ${isPrevIncomingSameSender ? "tt-chat-room-bubble-row--grouped" : ""}`}
                                    >
                                        <div className={`tt-chat-room-bubble ${isOutgoing ? "tt-chat-room-bubble--outgoing" : "tt-chat-room-bubble--incoming"}`}>
                                            <div className="tt-chat-room-bubble-text" dangerouslySetInnerHTML={{ __html: html }} />
                                            <div className={`tt-chat-room-bubble-time ${isOutgoing ? "tt-chat-room-bubble-time--outgoing" : "tt-chat-room-bubble-time--incoming"}`}>
                                                {time}
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

